import {
  continueLiveWithNextEpg,
  getAllEpgsForChannel,
  getLastWatchedEpgs,
  getLiveChannels,
  getMostWatchedEpgs,
  playVideo,
  removeUserDetails,
  resetEpgs,
  resetNextVideo,
  resetVideo,
  saveProfileSettings,
  setChannelSetting,
  setCurrentVideo,
  setError,
  setNextVideo,
  setPlayerControlsState,
  setPlayerPlaying,
  setVideoHash,
  setVideoOptions,
  setVideoPlayerEpgs,
  stopVideo,
  translationPacket,
  unStopVideo
} from 'common/actions';
import { Epg, LiveChannel, LiveChannels } from 'common/constants/data-types';
import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useStore } from 'react-redux';
import './Player.styles';

import TabLimitIcon from '@assets/media/tab-limit-icon.svg';
import Hls, {
  AudioTracksUpdatedData,
  AudioTrackSwitchedData,
  ErrorData,
  Level,
  LevelSwitchedData,
  SubtitleTracksUpdatedData,
  SubtitleTrackSwitchData
} from '@mediahub-bg/hls.js';
import {
  FocusContext,
  pause as pauseFocusable,
  resume as resumeFocusable,
  setFocus,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';
import { LoadingIndicator } from 'common/components/common/style';
import { FullscreenError } from 'common/components/full-screen-error';
import { Image } from 'common/components/image';
import { NoNetworkPopup } from 'common/components/no-network-popup';
import Video, { parseError, THls, VideoRefObject } from 'common/components/video';
import {
  CHUNK_SECONDS,
  ERROR_SUBCODE_ACCESS_DENIES,
  ERROR_SUBCODE_STREAM_LIMIT,
  HAS_ACCESS,
  ROUTE_NO_SUBSCRIPTION,
  SECONDS_TO_REVERT,
  STATUS_CODE_BAD_REQUEST,
  SUBTITLE_OFF_ID,
  TIME_BEFORE_BUFFER,
  TIME_TO_RECOVER
} from 'common/config/constants';
import variables from 'common/config/variables';
import { usePopup } from 'common/contexts/popup';
import { AppTheme } from 'common/interfaces';
import { FavoriteChannels } from 'common/reducers/favoriteChannels';
import {
  ChannelSettingKeys,
  LANG_DEFAULT,
  RESOLUTION_AUTO,
  SUBTITLES_OFF
} from 'common/reducers/profileSettings';
import { IVideoPlayer, PlayerControlsState } from 'common/reducers/videoPlayer';
import {
  findClosestNumber,
  getLiveEpg,
  getLivePoint,
  getLiveTimestamp,
  getPlaylist,
  getTimeBeforeBuffer,
  parseHHMMSSToSeconds
} from 'common/services/helpers';
import {
  getClassName,
  getKeyDigit,
  getRemoteKeyName,
  screenSaverToggle,
  sortByPosition
} from 'common/utils';
import AnimationDurations from 'common/utils/AnimationDurations';
import { prefetchSource } from 'common/utils/hlsPrefetch';
import { setZapPreview } from './hooks/zapPreview';
import {
  useOnlineStatus,
  useParentalLock,
  usePlayerRecords,
  useRadioImage,
  useSetState,
  useUpdateScheduler,
  useVisibilityState
} from 'common/utils/hooks';
import { preloadImage } from 'common/utils/imagePreloader';
import { PromiseDispatch } from 'index';
import {
  debounce,
  DebouncedFunc,
  isEmpty,
  isNil,
  isNumber,
  isString,
  toNumber,
  uniqueId
} from 'lodash';
import { ThunkActionDispatch } from 'redux-thunk';
import { Controls } from './controls';
import ChannelAccessOverlay from './controls/ChannelAccessOverlay';
import LockChannelOverlay from './controls/LockChannelOverlay';
import UnsupportedCodecOverlay from './controls/UnsupportedCodecOverlay';
import VodOverlay from './controls/VodOverlay';
import useCurrentTime, { setProgrammeOffset } from './hooks/useCurrentTime';
const ZAP_LOGO_PRELOAD_RANGE = 8;
const PREFETCH_BEFORE_END = 4; // seconds

const {
  CONTROLS_EXPAND_DURATION,
  DEFAULT_ANIMATION_DURATION,
  ZAP_NO_KEYUP_TIMEOUT,
  ZAP_RELEASE_DELAY,
  ZAP_HOLD_RELEASE_DELAY,
  FAST_ZAP_NUMBER_INPUT_TIMEOUT,
  HANDLE_MEDIA_ERROR_TIMEOUT,
  MAX_FASTZAPP_LENGTH,
  PLAYER_EXPAND_DURATION,
  PLAY_PREVIOUS_THRESHOLD
} = variables;

interface PlayerProps {
  paused?: boolean;
  className?: string;
  onBack?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  // Redux injected
  currentAuthToken: string;
  playerControlsState: PlayerControlsState;
  allEpgs?: Epg[];
  videoSource?: string;
  videoStart: number;
  videoStop: number;
  videoPlaylistStart?: number;
  videoStartPosition: number;
  videoPosition?: number;
  channelId?: string;
  epgsObject?: IVideoPlayer['epgs'];
  isLiveEpg?: number;
  isOpen?: boolean;
  isFullscreen?: boolean;
  isPlaying?: boolean;
  isStopped?: boolean;
  videoPaused?: boolean;
  hasNextVideo: boolean;
  videoNow?: number;
  videoAccess?: number;
  videoLocked?: boolean;
  liveChannels: LiveChannels;
  audioTracks: IVideoPlayer['video']['audioTracks'];
  subtitleTracks: IVideoPlayer['video']['subtitleTracks'];
  logos: any;
  videoHash?: string;
  i18n: typeof translationPacket;
  profileSettings: any;
  channelSettings: any;
  setPlayerPlaying: (state: boolean) => void;
  getAllEpgsForChannel: any;
  setVideoPlayerEpgs: any;
  setCurrentVideo: any;
  setVideoOptions: typeof setVideoOptions;
  resetVideo: VoidFunction;
  removeUserDetails: VoidFunction;
  setError: (code: number, redirect?: string) => void;
  setPlayerControlsState: (state: PlayerControlsState) => void;
  getLiveChannels: PromiseDispatch;
  resetEpgs: () => void;
  setNextVideo: (epg: Epg, position: number, startPosition?: number, autoPlay?: boolean) => void;
  continueLiveWithNextEpg: (epg: Epg) => void;
  resetNextVideo: () => void;
  playVideo: (currentepg: Epg, options?: any) => void;
  stopVideo: VoidFunction;
  unStopVideo: VoidFunction;
  getMostWatchedEpgs: PromiseDispatch;
  getLastWatchedEpgs: PromiseDispatch;
  setChannelSetting: ThunkActionDispatch<typeof setChannelSetting>;
  saveProfileSettings: ThunkActionDispatch<typeof saveProfileSettings>;
  setVideoHash: (hash?: number) => void;
  favoriteChannels: FavoriteChannels;
  vod?: boolean;
  vodAsset?: Epg;
}

interface PlayerState {
  videoHasFatalError?: boolean;
  fastZapNumber?: number;
  canSupportNativeHls?: boolean;
  showBuffer?: boolean;
}

const Player = (props: PlayerProps) => {
  const store = useStore<PlayerStore>();
  const [state, setState] = useSetState<PlayerState>({
    videoHasFatalError: false,
    canSupportNativeHls: false,
    showBuffer: false
  });

  const {
    ref: focusRef,
    focusKey,
    hasFocusedChild
  } = useFocusable({
    focusKey: 'PLAYER',
    trackChildren: true,
    isFocusBoundary: true,
    focusable: props.isFullscreen,
    onBlur: props.onBlur
  });
  const { setVideoRefObject, setCurrentTime: setCurrentTimeFromHook } = useCurrentTime(false);
  // set during render so time checks never mix two shows
  const programmeOffsetRef = useRef(0);
  programmeOffsetRef.current =
    props.videoPlaylistStart && props.videoStart > props.videoPlaylistStart
      ? props.videoStart - props.videoPlaylistStart
      : 0;
  setProgrammeOffset(programmeOffsetRef.current);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hls = useRef<THls | null>(null);
  const liveChannelsRef = useRef(props.liveChannels);
  liveChannelsRef.current = props.liveChannels;
  const videoHandleRef = useRef<VideoRefObject | null>(null);
  const subtitleCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const recoverStartTime = useRef<number | null>(null);
  const recoverSwapAudioCodecDate = useRef<number | null>(null);
  const recoverDecodingErrorDate = useRef<number | null>(null);
  const controlsTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const autoFocusTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

  const mediaPreloadTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const noNetworkPopupTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const mediaErrorHandlerTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const holdingOkTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const pressedOkRef = useRef(false);
  const debouncedCalls = useRef<DebouncedFunc<VoidFunction> | undefined>(undefined);
  const channelListDebounce = useRef<DebouncedFunc<VoidFunction> | undefined>(undefined);
  const pendingZapRef = useRef(false);
  const zapTargetPositionRef = useRef<number | undefined>(undefined);
  // on slow TVs keyup can arrive before the next video reaches the store
  const zapTargetRef = useRef<LiveChannel | undefined>(undefined);
  const zapCommitTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const zapKeyDownRef = useRef(false);
  const zapHeldRef = useRef(false);
  const playerControlsStateRef = useRef(props.playerControlsState);
  const playerIsFullscreenRef = useRef(props.isFullscreen);
  const playerIsPlayingRef = useRef(props.isPlaying);
  const videoPausedRef = useRef(props.videoPaused);
  const stateRef = useRef(state);
  const currentRecreatableStream = useRef<(Epg & { vod?: boolean }) | undefined>(undefined);
  const vodRef = useRef(props.vod);
  const channelIdRef = useRef(props.channelId);
  const lastWatchedCurrentTimestampRef = useRef<number>(0);
  const { sendPlayerProgress } = usePlayerRecords(props.epgsObject?.current);
  const currentAuthTokenRef = useRef(props.currentAuthToken);

  const epgsObjectRef = useRef(props.epgsObject);

  useEffect(() => {
    epgsObjectRef.current = props.epgsObject;
    return () => {
      epgsObjectRef.current = undefined;
    };
  }, [props.epgsObject]);

  const epgsLoaded = useMemo(() => {
    // If loading VOD check if we have available EPGS
    if (props.vod) return !isEmpty(props.allEpgs);

    // If loading live channel check if we have more than 1 EPGs to show
    // TODO: update in the future for better logic
    // If some channels have only 1 EPG, we should check if the EPG is live or not and show the overlay accordingly
    return !isEmpty(props.allEpgs) && !props.vod && props.allEpgs!.length > 1;
  }, [props.allEpgs, props.vod]);

  const { cancel: cancelScheduler, forceStart: forceStartScheduler } = useUpdateScheduler(
    !props.isPlaying || props.playerControlsState === 'channels'
  );
  const [isOnline] = useOnlineStatus();
  const isOnlineRef = useRef(isOnline);

  const isAppActive = useVisibilityState({ onVisible: forceStartScheduler });
  const radioImages = useRadioImage(props.channelId as string);
  const { openPopup, closePopup } = usePopup();
  const {
    locked: channelsLocked,
    unlock: temporarilyUnlock,
    lockChannelsBack,
    getChannelsLockedState
  } = useParentalLock();

  const [showUnlockOverlay, setShowUnlockOverlay] = useState(false);
  const showUnlockOverlayRef = useRef(showUnlockOverlay);
  const [showUnsupportedCodecOverlay, setShowUnsupportedCodecOverlay] = useState(false);
  const lastRequestedEpgsTimestampRef = useRef<number>(0);

  const showPaidOverlay = useMemo(() => {
    return !props.videoAccess && props.videoSource;
  }, [props.videoSource, props.videoAccess]);

  // Show overlay if channel is locked by parental control or video is locked by content protection and
  // hide controls if any of those conditions are true
  const hasPlayerOverlay = showUnlockOverlay || showUnsupportedCodecOverlay || showPaidOverlay;

  const videoSourceRef = useRef<string | undefined>(undefined);
  const videoSource = useMemo(() => {
    const value =
      props.videoAccess === HAS_ACCESS && !hasPlayerOverlay && !showUnsupportedCodecOverlay
        ? props.videoSource
        : undefined;
    videoSourceRef.current = value;
    return value;
  }, [props.videoAccess, props.videoSource, hasPlayerOverlay, showUnsupportedCodecOverlay]);

  useEffect(() => {
    currentAuthTokenRef.current = props.currentAuthToken;
  }, [props.currentAuthToken]);

  useEffect(() => {
    const lockedState = getChannelsLockedState();
    const status = !!(
      // !isEmpty(props.videoSource) &&
      (
        props.videoLocked &&
        !isEmpty(props.channelId) &&
        lockedState &&
        // Show unlock overlay only when video is not paid
        !showPaidOverlay
      )
    );
    setShowUnlockOverlay(status);
    // Reset next video when video is locked
    if (status) {
      props.resetNextVideo();
    }
  }, [props.videoLocked, props.channelId, showPaidOverlay]);

  // Listen for channel unlock flag
  useEffect(() => {
    if (!channelsLocked) {
      setShowUnlockOverlay(false);
    }
  }, [channelsLocked]);

  useEffect(() => {
    showUnlockOverlayRef.current = showUnlockOverlay;
  }, [showUnlockOverlay]);

  useEffect(() => {
    vodRef.current = props.vod;
  }, [props.vod]);

  useEffect(() => {
    channelIdRef.current = props.channelId;
  }, [props.channelId]);

  useEffect(() => {
    videoPausedRef.current = props.videoPaused;
  }, [props.videoPaused]);

  // Reset flags when changing source
  useEffect(() => {
    setShowUnsupportedCodecOverlay(false);
  }, [props.videoSource]);

  const channelAccessRef = useRef(false);
  const channelAccess = useMemo(() => {
    const value =
      !isNil(props.videoAccess) && props.videoAccess === HAS_ACCESS && !hasPlayerOverlay;
    channelAccessRef.current = value;
    return value;
  }, [props.videoAccess, props.channelId, hasPlayerOverlay]);

  const currentChannelPosition = useMemo(() => {
    if (isNil(props.channelId)) return undefined;

    const currentChannelId = props.channelId;
    // Show favorite position if channel is added to the list
    const favoritePosition = props.favoriteChannels[currentChannelId]?.position;
    const explicitPosition = props.videoPosition;
    const originalPosition = props.liveChannels[currentChannelId]?.position;

    if (!isNil(explicitPosition)) return explicitPosition;
    if (!isNil(favoritePosition)) return favoritePosition;

    return originalPosition;
  }, [props.favoriteChannels, props.liveChannels, props.videoPosition, props.channelId]);

  const vodSelectionPageRef = useRef(false);
  const vodSelectionPage = useMemo(() => {
    const value = !!(
      props.vod &&
      props.isOpen &&
      (!videoSource || props.isStopped) &&
      props.vodAsset
    );
    vodSelectionPageRef.current = value;
    return value;
  }, [props.vod, props.isOpen, props.isPlaying, props.vodAsset, videoSource, props.isStopped]);

  // Pipe onFocus to parent from children
  useEffect(() => {
    if (hasFocusedChild) {
      props.onFocus && props.onFocus();
    }
  }, [hasFocusedChild]);

  const currentChannelPositionRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    currentChannelPositionRef.current = currentChannelPosition;
  }, [currentChannelPosition]);

  useEffect(() => {
    isOnline && forceStartScheduler();
    isOnlineRef.current = isOnline;
  }, [isOnline]);

  useEffect(() => {
    return () => {
      if (hls.current || state.canSupportNativeHls) {
        props.resetVideo();
      }
    };
  }, [hls]);

  useEffect(() => {
    lastRequestedEpgsTimestampRef.current = 0;
  }, [props.channelId]);

  useEffect(() => {
    const sameChannel = props.vod ? true : props.allEpgs?.[0]?.chan_id === props.channelId;
    // Load EPGs for current channel after video starts playing with a delay to prevent overload when zapping fast between channels
    if (epgsLoaded && sameChannel) {
      setEpgs(props.videoStart);
    }
  }, [props.allEpgs, props.videoStart, props.channelId, props.vod, epgsLoaded]);

  useEffect(() => {
    if (videoSource) {
      if (props.isOpen) {
        loadVideo();
      }
    }
    return () => {
      playerIsPlayingRef.current && handleBeforeLoadNewVideo();
    };
  }, [videoSource]);

  useEffect(() => {
    playerIsPlayingRef.current = props.isPlaying;
    // Start interval to send player progress when video starts playing
    let id: NodeJS.Timeout | undefined = undefined;
    if (props.isPlaying) {
      id = setInterval(() => {
        sendPlayerProgress(getCurrentTime());
      }, variables.PLAYER_RECORDS_UPDATE_INTERVAL);
    }
    return () => {
      if (id) {
        clearInterval(id);
      }
    };
  }, [props.isPlaying]);

  // Video is reset
  useEffect(() => {
    if (!props.isOpen && !props.isPlaying && !props.isStopped) {
      setState({
        videoHasFatalError: false,
        canSupportNativeHls: false,
        showBuffer: false
      });
      currentRecreatableStream.current = undefined;
      clearNoNetworkPopup();
      clearPreloadTimeout();
    }
  }, [props.isOpen, props.isPlaying, props.isStopped]);

  const handleBeforeLoadNewVideo = useCallback(() => {
    sendPlayerProgress(lastWatchedCurrentTimestampRef.current);
  }, [sendPlayerProgress]);

  const changeVideo = useCallback(
    (
      channelId: string,
      epg: Epg,
      position?: number,
      startPosition?: number,
      options: object = {}
    ) => {
      const { start, stop } = epg;
      props.setCurrentVideo({
        currentEpg: epg,
        isOpen: true,
        isPlaying: false,
        channelId,
        video: {
          start,
          stop,
          startPosition: startPosition ? startPosition : 0,
          channelPosition: position,
          isFullscreen: playerIsFullscreenRef.current,
          ...options
        }
      });
    },
    []
  );

  // Store as a ref the current stream that can be recreated
  useEffect(() => {
    if (props.channelId && !isEmpty(props.channelId) && !isEmpty(props.epgsObject?.current)) {
      if (props.epgsObject?.current?.eventid) {
        currentRecreatableStream.current = {
          ...props.epgsObject.current,
          chan_id: props.channelId,
          vod: !!props.vod
        };
      } else {
        console.warn('Missing required eventid for current epg.');
      }
    }
  }, [props.channelId, props.epgsObject?.current, props.vod]);

  // Try to refresh current stream with new URL
  useEffect(() => {
    if (!playerIsPlayingRef.current || !props.isOpen) return;

    if (!isEmpty(currentRecreatableStream.current) && props.currentAuthToken) {
      const { chan_id, vod, access } = currentRecreatableStream.current;
      changeVideo(
        chan_id,
        currentRecreatableStream.current,
        currentChannelPositionRef.current,
        getCurrentTime(),
        {
          vod,
          access
        }
      );
    }
  }, [props.currentAuthToken, props.i18n?.errorPage?.playerCanNotReloadMedia]);

  // Make transition to fullscreen
  useEffect(() => {
    if (props.isFullscreen) {
      debounce(() => {
        videoRef.current?.focus();
        playerIsPlayingRef.current && onCanPlay();
      }, PLAYER_EXPAND_DURATION)();
    }
    playerIsFullscreenRef.current = props.isFullscreen;
  }, [props.isFullscreen, videoRef.current]);

  // Make sure the player has focus
  useEffect(() => {
    hasFocusedChild && videoRef.current?.focus();
  }, [hasFocusedChild, videoRef.current]);

  useEffect(() => {
    playerControlsStateRef.current = props.playerControlsState;
    refreshControlsTimeout();
  }, [props.playerControlsState]);

  // Update state current time
  useEffect(() => {
    if (props.playerControlsState === 'seeking') {
      timeUpdate();
    }
  }, [props.playerControlsState]);

  //Handle case when current channel has no ACCESS or video is LOCKED
  useEffect(() => {
    if (hasPlayerOverlay) {
      props.setPlayerControlsState('hidden');
    }
  }, [hasPlayerOverlay, videoRef, props.playerControlsState]);

  // Force player to show controls when vod selection page is open
  // and player is not in expanded or bottom-view state
  // This is to prevent the player from being hidden when navigating to vod selection page
  useEffect(() => {
    if (
      props.isFullscreen &&
      vodSelectionPage &&
      !['expanded', 'bottom-view'].includes(props.playerControlsState)
    ) {
      props.setPlayerControlsState('expanded');
      setFocus('VOD_PLAY');
    }
  }, [props.playerControlsState, vodSelectionPage, props.isFullscreen]);

  const showControls = useCallback((mini?: boolean) => {
    const newState = mini ? 'mini' : 'expanded';
    newState !== playerControlsStateRef.current &&
      channelAccessRef.current &&
      startTransition(() => {
        props.setPlayerControlsState(newState);
        clearTimeout(autoFocusTimeout.current);
        autoFocusTimeout.current = setTimeout(
          () =>
            playerIsFullscreenRef.current &&
            newState === 'expanded' &&
            setFocus(vodSelectionPageRef.current ? 'VOD_PLAY' : 'TOGGLE_PLAY'),
          DEFAULT_ANIMATION_DURATION
        );
      });
  }, []);

  const hideControls = useCallback(() => {
    clearTimeout(controlsTimeout.current);
    // When closing channel list first show mini view
    if (playerControlsStateRef.current === 'channels') {
      props.setPlayerControlsState('mini');
      setTimeout(() => {
        props.setPlayerControlsState('hidden');
      }, CONTROLS_EXPAND_DURATION);
    } else {
      props.setPlayerControlsState('hidden');
    }
  }, []);

  const hideControlsWithTimeout = useCallback((mini?: boolean, timeout?: number) => {
    const timerValue =
      timeout ||
      (mini
        ? AnimationDurations.get('PLAYER_MINIVIEW_AUTOHIDE_TIMER')
        : AnimationDurations.get('PLAYER_AUTOHIDE_TIMER'));
    clearTimeout(controlsTimeout.current);
    // Disable auto hide when player is in vod selection page
    if (!vodSelectionPageRef.current) {
      controlsTimeout.current = setTimeout(() => {
        const toHideControls = !['seeking'].includes(playerControlsStateRef.current);
        toHideControls && props.setPlayerControlsState('hidden');
      }, timerValue);
    }
    // Reset both timer values
    AnimationDurations.reset('PLAYER_MINIVIEW_AUTOHIDE_TIMER');
    AnimationDurations.reset('PLAYER_AUTOHIDE_TIMER');
    //
  }, []);

  const toggleControls = useCallback(
    (mini?: boolean, timeout?: number) => {
      clearOkHold();
      showControls(mini);
      hideControlsWithTimeout(mini, timeout);
    },
    [showControls, hideControlsWithTimeout]
  );

  const refreshControlsTimeout = useCallback(() => {
    // Refresh timeout
    hideControlsWithTimeout(playerControlsStateRef.current === 'mini');
  }, [hideControlsWithTimeout]);

  const getCurrentTime = () => {
    if (videoRef.current) {
      return Math.max(0, videoRef.current.currentTime - programmeOffsetRef.current);
    }
    return 0;
  };

  const play = useCallback(async () => {
    try {
      if (!videoRef.current) {
        return;
      }
      await videoRef.current.play();
      props.setVideoOptions({
        paused: false
      });
      props.unStopVideo();
      screenSaverToggle(false);
    } catch (error) {
      console.info(error);
    }
  }, []);

  const pause = useCallback(() => {
    try {
      if (!videoRef.current) {
        return;
      }
      videoRef.current.pause();
      props.setVideoOptions({
        paused: true
      });
      screenSaverToggle(true);
    } catch (error) {
      console.info(error);
    }
  }, []);

  useEffect(() => {
    if (!isNil(props.paused) && props.isPlaying) {
      props.paused ? pause() : play();
    }
  }, [props.paused]);

  useEffect(() => {
    if (props.isPlaying) props.isStopped ? pause() : play();
  }, [props.isStopped, props.isPlaying]);

  const togglePlay = useCallback(
    (toPlay?: boolean) => {
      const paused = isNil(toPlay) ? videoRef?.current?.paused : toPlay;
      if (paused) {
        play();
      } else {
        pause();
      }
    },
    [videoRef.current, play, pause]
  );

  const clearTimers = useCallback(() => {
    if (!hasFocusedChild) {
      clearOkHold();
    }
  }, [hasFocusedChild]);

  useEffect(() => {
    !hasFocusedChild && clearTimers();
  }, [hasFocusedChild]);

  const closePlayer = useCallback(() => {
    props.onBack && props.onBack();
    hideControls();
    cancelScheduler();
    clearTimers();
  }, [clearTimers, hideControls, props.onBack, props.videoLocked]);

  useEffect(() => {
    if (!isAppActive) {
      props.isFullscreen && closePlayer();
      props.resetVideo();
      cancelScheduler();
    }
  }, [isAppActive, props.isFullscreen, props.onBack]);

  const onSubtitlesUpdated = useCallback(
    (data: SubtitleTracksUpdatedData) => {
      subtitleCanvasRef.current && hls.current?.attachDvbCanvas(subtitleCanvasRef.current);
      props.setVideoOptions({
        subtitleTracks: data.subtitleTracks || [],
        currentSubtitleTrack: hls.current?.subtitleTrack || SUBTITLE_OFF_ID
      });
    },
    [hls.current?.subtitleTrack]
  );

  const onSubtitleTrackSwitch = useCallback((data: SubtitleTrackSwitchData) => {
    props.setVideoOptions({
      currentSubtitleTrack: data.id
    });
  }, []);

  // Handle initial dvbAudioTrack and autoLevel
  useEffect(() => {
    props.setVideoOptions({
      currentAudioTrack: hls.current?.dvbAudioTrack,
      autoLevel: hls.current?.autoLevelEnabled
    });
  }, []);

  const onDvbAudioTracksUpdated = useCallback(
    (data: AudioTracksUpdatedData) => {
      props.setVideoOptions({
        audioTracks: data.audioTracks.length > 0 ? data.audioTracks : [],
        currentAudioTrack: hls.current?.dvbAudioTrack
      });
    },
    [hls.current]
  );

  const onDvbAudioTrackSwitch = useCallback((data: AudioTrackSwitchedData) => {
    props.setVideoOptions({
      currentAudioTrack: data.id
    });
  }, []);

  // a preloaded zap's store update, applied once the media seeks
  const pendingZapStoreUpdateRef = useRef<(() => void) | undefined>(undefined);
  const afterZapUpdatesRef = useRef<(() => void)[]>([]);
  const flushZapStoreUpdate = () => {
    const update = pendingZapStoreUpdateRef.current;
    pendingZapStoreUpdateRef.current = undefined;
    update?.();
    const updates = afterZapUpdatesRef.current;
    afterZapUpdatesRef.current = [];
    updates.forEach((u) => u());
  };
  // store updates that would re-render the player while a zap is still loading
  const afterZapStoreUpdate = (update: () => void) => {
    if (pendingZapStoreUpdateRef.current) {
      afterZapUpdatesRef.current.push(update);
    } else {
      update();
    }
  };

  const onLevelsUpdated = useCallback((levels: Level[], autoLevel?: boolean) => {
    afterZapStoreUpdate(() => props.setVideoOptions({ levels, autoLevel }));
  }, []);

  const onLevelSwitched = useCallback((data: LevelSwitchedData, autoLevel?: boolean) => {
    afterZapStoreUpdate(() => props.setVideoOptions({ currentLevel: data.level, autoLevel }));
  }, []);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const onBufferAppended = useCallback(() => {
    // Remove loading when new buffer is received after fatal error recover
    clearMediaError();
    clearNoNetworkPopup();
    if (stateRef.current.videoHasFatalError) {
      // recoverSwapAudioCodecDate.current = null;
      // recoverDecodingErrorDate.current = null;
      setState({ videoHasFatalError: false });
    }
  }, []);

  const handleCodecCheck = useCallback((supported: boolean) => {
    setShowUnsupportedCodecOverlay(!supported);
  }, []);

  const loadVideo = useCallback(() => {
    const videoOptions = {
      startPosition: isNil(props.videoStartPosition) ? -1 : props.videoStartPosition,
      duration: props.videoStop - props.videoStart,
      ended: false,
      paused: false,
      autoPaused: false,
      waiting: false,
      hasStarted: true,
      isLiveEpg: props.videoNow,
      // isFullscreen: props.isFullscreen,
      muted: false
    };

    // a reused MediaSource still holds the previous channel, hls.js seeks once ready
    if (!videoRef.current || videoRef.current.readyState === HTMLMediaElement.HAVE_NOTHING) {
      const currentTime =
        videoOptions.isLiveEpg && videoOptions.startPosition === -1
          ? getLiveTimestamp() - props.videoStart
          : Math.max(props.videoStartPosition, 0);
      setCurrentTimeFromHook(currentTime);
    }
    props.setVideoOptions({
      ...videoOptions
    });
  }, [
    props.videoStartPosition,
    props.videoStart,
    props.videoStop,
    props.videoNow,
    props.isFullscreen,
    recoverStartTime.current
  ]);

  const handleHlsError = useCallback(
    (data: ErrorData) => {
      switch (data.details) {
        case Hls.ErrorDetails.BUFFER_APPENDING_ERROR:
          // Force reload media on buffer appending error
          handleMediaError();
          break;
        case Hls.ErrorDetails.BUFFER_STALLED_ERROR:
          handleBufferStall(data);
          break;
        default:
          break;
      }

      if (data.fatal) {
        const now = performance.now();
        switch (data.type) {
          case Hls.ErrorTypes.MEDIA_ERROR:
            setState({ videoHasFatalError: true });
            if (
              !recoverDecodingErrorDate.current ||
              now - recoverDecodingErrorDate.current > TIME_TO_RECOVER + CHUNK_SECONDS * 1000
            ) {
              recoverDecodingErrorDate.current = performance.now();
              console.warn('Trying to recover media error.');
              const time = getCurrentTime() + CHUNK_SECONDS;
              hls.current?.recoverMediaError();
              setCurrentTime(time);
            } else {
              if (
                !recoverSwapAudioCodecDate.current ||
                now - recoverSwapAudioCodecDate.current > TIME_TO_RECOVER
              ) {
                recoverSwapAudioCodecDate.current = performance.now();
                console.warn('Trying to swap audio codecs and recover media error.');

                // TODO add message Trying to swap audio codecs and recover media error.
                hls.current?.swapAudioCodec();
                reloadMedia();
              } else {
                console.warn('Cannot recover. Last media error recovery failed.');
              }
            }
            break;
          case Hls.ErrorTypes.NETWORK_ERROR:
            handleHlsNetworkError({ ...data });
            break;
          default:
            // TODO add message A network error occurred, try to recover
            hls.current?.destroy();
            break;
        }
      } else {
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            handleNonFatalNetworkError({ ...data });
            break;
        }
      }
    },
    [
      videoRef.current,
      hls.current,
      recoverDecodingErrorDate.current,
      recoverSwapAudioCodecDate.current,
      mediaErrorHandlerTimeout.current
    ]
  );

  const reloadMedia = useCallback((start?: number) => {
    props.setPlayerPlaying(false);
    const videoSource = new URL(videoSourceRef.current ?? '');
    if (videoSource.searchParams.get('access_token') !== currentAuthTokenRef.current) {
      videoSource.searchParams.set('access_token', currentAuthTokenRef.current);
      hls.current?.loadSource(videoSource.toString());
    } else {
      hls.current?.media?.buffered.length === 0 && props.setVideoHash();
      hls.current?.recoverMediaError();
    }
  }, []);

  const handleBufferStall = useCallback((data: ErrorData) => {
    try {
      // Try to recover on non-fatal stall
      if (!data.fatal) {
        const hasEnoughBuffer = !!hls.current?.media?.buffered?.length;

        // If we have enough buffer, try to play as on
        // some devices video element pauses itself on stall
        // only try to play if video is not paused by user
        hasEnoughBuffer && videoPausedRef.current === false && videoRef.current?.play();
      }
    } catch (e) {
      console.warn('Error handling buffer stall', e);
    }
    // Handle normally as media error
    handleMediaError();
  }, []);

  const handleMediaError = useCallback((forceReload?: boolean) => {
    clearMediaError();
    if (forceReload) {
      reloadMedia();
      return;
    }
    mediaPreloadTimeout.current = setTimeout(() => {
      setState({ showBuffer: true });
      handleNoNetworkPopup();
    }, variables.PRELOAD_TIMEOUT);
    mediaErrorHandlerTimeout.current = setTimeout(reloadMedia, HANDLE_MEDIA_ERROR_TIMEOUT);
  }, []);

  const clearPreloadTimeout = useCallback(() => {
    mediaPreloadTimeout.current && clearTimeout(mediaPreloadTimeout.current);
    mediaPreloadTimeout.current = undefined;
  }, []);

  const clearBuffering = useCallback(() => {
    clearPreloadTimeout();
    stateRef.current.showBuffer && setState({ showBuffer: false });
  }, []);

  const clearMediaError = useCallback(() => {
    clearBuffering();
    mediaErrorHandlerTimeout.current && clearTimeout(mediaErrorHandlerTimeout.current);
    mediaErrorHandlerTimeout.current = undefined;
  }, []);

  const clearNoNetworkPopup = useCallback(() => {
    noNetworkPopupTimeout.current && clearTimeout(noNetworkPopupTimeout.current);
    noNetworkPopupTimeout.current = undefined;
    closePopup('no-network');
  }, []);

  const handleNoNetworkPopup = useCallback(() => {
    if (noNetworkPopupTimeout.current) return;

    noNetworkPopupTimeout.current = setTimeout(() => {
      openPopup({
        id: 'no-network',
        content: <NoNetworkPopup />,
        onClose: () => {
          noNetworkPopupTimeout.current = undefined;
        }
      });
    }, variables.PRELOAD_TIMEOUT);
  }, []);

  const setEpgs = useCallback(
    (start?: number) => {
      props.setVideoPlayerEpgs(props.channelId, start);
    },
    [props.channelId]
  );

  const handleChannelAccess = useCallback(async () => {
    const liveChannels = (await props.getLiveChannels()) as LiveChannels;
    if (!channelIdRef.current) return;
    const access = liveChannels?.[channelIdRef.current]?.access === HAS_ACCESS;
    if (access !== channelAccessRef.current && !access) {
      props.setError(ERROR_SUBCODE_ACCESS_DENIES, ROUTE_NO_SUBSCRIPTION);
      props.resetVideo();
      clearMediaError();
    } else if (access) {
      clearMediaError();
      reloadMedia();
    }
  }, []);

  const handleHlsNetworkError = useCallback((data: ErrorData) => {
    const error = parseError(data);

    if (error?.status === STATUS_CODE_BAD_REQUEST && error?.subcode) {
      console.warn('fatal network error', error);
      switch (error.subcode) {
        case ERROR_SUBCODE_ACCESS_DENIES:
          {
            handleChannelAccess();
          }
          break;
        default:
          console.warn('A network error occurred, trying to recover');
          reloadMedia();
          break;
      }
    } else {
      handleMediaError();
    }
  }, []);

  const handleNonFatalNetworkError = useCallback(
    (data: ErrorData) => {
      const error = parseError(data);

      if (error?.status === STATUS_CODE_BAD_REQUEST && error?.subcode) {
        switch (error.subcode) {
          case ERROR_SUBCODE_STREAM_LIMIT:
            {
              pause();
              const onStop = () => {
                closePopup('TAB_LIMIT');
                closePlayer();
                props.resetVideo();
              };
              const onContinue = () => {
                epgsObjectRef?.current?.current &&
                  props.playVideo(epgsObjectRef.current.current, {
                    startPosition: getCurrentTime(),
                    hash: uniqueId()
                  });
                closePopup('TAB_LIMIT');
              };
              openPopup({
                fullscreen: true,
                id: 'TAB_LIMIT',
                background: 'black',
                content: (
                  <FullscreenError
                    icon={TabLimitIcon}
                    title={props.i18n.authentication.tabLimit}
                    description={props.i18n.authentication.tabLimitDescription}
                    info={props.i18n.authentication.tabLimitInfo}
                    primaryButtonTitle={props.i18n.authentication.tabLimitPrimaryButton}
                    secondaryButtonTitle={props.i18n.authentication.tabLimitSecondaryButton}
                    onPrimaryButtonPress={onContinue}
                    onSecondaryButtonPress={onStop}
                  />
                )
              });
            }
            break;
        }
      }
    },
    [props.i18n.authentication, openPopup, closePopup, closePlayer, play, pause]
  );

  const forward = useCallback(
    (seconds: number) => {
      if (!videoRef.current || !playerIsPlayingRef.current) {
        return;
      }
      let secondsToForward = getCurrentTime() + seconds;
      const duration = getDuration();
      const timeBeforeBuffer = getTimeBeforeBuffer(getCurrentTime(), duration);
      if (props.isLiveEpg && timeBeforeBuffer < SECONDS_TO_REVERT) {
        secondsToForward = getCurrentTime() + timeBeforeBuffer;
      }
      if (vodRef.current && getCurrentTime() + seconds >= duration) {
        secondsToForward = duration;
      }
      setCurrentTime(secondsToForward);
    },
    [props.isLiveEpg]
  );

  const backward = useCallback((seconds: number) => {
    if (!videoRef.current || !playerIsPlayingRef.current) {
      return;
    }
    setCurrentTime(getCurrentTime() - seconds);
  }, []);

  const setCurrentTime = useCallback(
    (value: number, onSeeked?: () => void) => {
      if (!videoRef.current) {
        return;
      }
      // Calculate duration based on current epg if live
      // in order to play real future show
      // sometimes duration from video element is lower than real live point
      // Fallback to video duration if no current epg
      const duration = isEmpty(epgsObjectRef.current?.current)
        ? getDuration()
        : epgsObjectRef.current!.current.stop - epgsObjectRef.current!.current.start;

      const delta = value - duration;
      if (value < 0 && playerIsPlayingRef.current) {
        playPrevious(Math.abs(value));
        onSeeked?.();
        return;
      } else if (value > duration && playerIsPlayingRef.current && !vodRef.current) {
        playNext(Math.abs(delta));
        onSeeked?.();
        return;
      } else if (isFinite(value)) {
        videoRef.current.currentTime = value + programmeOffsetRef.current;
        videoRef.current.addEventListener(
          'seeked',
          function handler() {
            onSeeked?.();
            videoRef.current?.removeEventListener('seeked', handler);
          },
          { once: true }
        );
        // Manually update current time in the hook
        setCurrentTimeFromHook(value);
        //
      }
      if (props.isLiveEpg) {
        progress();
      }
    },
    [videoRef.current, props.isLiveEpg]
  );

  const setVideoToLive = useCallback(() => {
    const allEpgs = epgsObjectRef.current?.all as Epg[] | undefined;
    const epgs = epgsObjectRef.current;
    if (allEpgs && epgs) {
      // find epg that is live streaming
      const now = getLivePoint();
      const currentEpg = allEpgs.find((epg) => epg.start < now && epg.stop > now);
      if (currentEpg) {
        const current = epgs.current;
        // Check if the epg is the same as the one playing one
        currentEpg.start !== current.start
          ? changeVideo(currentEpg.chan_id, currentEpg, currentChannelPositionRef.current, -1)
          : setCurrentTime(getDuration() - TIME_BEFORE_BUFFER);
      }
    }
  }, [changeVideo]);

  const setVideoToBegining = useCallback(() => {
    // seeking before the first chunk makes hls.js jump to the live edge
    const firstChunk = hls.current?.latestLevelDetails?.fragmentStart ?? 0;
    setCurrentTime(Math.max(0, firstChunk + 1 - programmeOffsetRef.current));
  }, []);

  /**
   * Get video duration
   * @returns {*}
   */
  const getDuration = useCallback(() => {
    if (!videoRef.current) {
      return 0;
    }
    return Number.isFinite(videoRef.current.duration)
      ? Math.max(0, videoRef.current.duration - programmeOffsetRef.current)
      : parseHHMMSSToSeconds(epgsObjectRef.current?.current?.duration || '0:00:00');
  }, [videoRef.current]);

  const ended = useCallback(() => {
    const next = epgsObjectRef.current?.next;
    const current = epgsObjectRef.current?.current;
    // the live playlist continues into the next show
    if (
      next &&
      current &&
      props.videoNow &&
      !vodRef.current &&
      (next.chanId || next.chan_id) === channelIdRef.current &&
      next.start <= current.stop
    ) {
      props.continueLiveWithNextEpg(next);
      return;
    }
    next &&
      changeVideo(
        vodRef.current ? next.id : next.chanId,
        next,
        currentChannelPositionRef.current,
        0,
        {
          vod: vodRef.current,
          access: next.access
        }
      );
  }, [changeVideo, props.videoNow]);

  const playPrevious = useCallback(
    (fromEnd?: number) => {
      try {
        const prev = epgsObjectRef.current?.prev;
        if (!prev) {
          return;
        }
        const startPosition = fromEnd ? prev.stop - prev.start - fromEnd : 0;
        changeVideo(prev.chanId, prev, currentChannelPositionRef.current, startPosition);
      } catch (error) {
        console.warn(error);
      }
    },
    [changeVideo]
  );

  const playNext = useCallback(
    (fromStart?: number) => {
      try {
        const next = epgsObjectRef.current?.next;
        if (!next) {
          return;
        }
        const startPosition = fromStart ? fromStart - TIME_BEFORE_BUFFER : 0;
        changeVideo(
          vodRef.current ? next.id : next.chanId,
          next,
          currentChannelPositionRef.current,
          startPosition,
          {
            vod: vodRef.current,
            access: next.access
          }
        );
      } catch (error) {
        console.warn(error);
      }
    },
    [changeVideo]
  );

  const timeUpdate = useCallback(() => {
    // Skip function if video is not playing, or still belongs to the previous channel
    if (!playerIsPlayingRef.current || pendingZapStoreUpdateRef.current) {
      return;
    }

    const currentTime = getCurrentTime();
    lastWatchedCurrentTimestampRef.current = currentTime;

    if (props.videoNow && currentTime >= props.videoStop - props.videoStart) {
      ended();
    }
    // prefetch the next archived show before this one ends
    const next = epgsObjectRef.current?.next;
    const nextChannelId = next && (next.chanId || next.chan_id);
    if (
      !props.videoNow &&
      !vodRef.current &&
      next &&
      nextChannelId === channelIdRef.current &&
      getDuration() - currentTime < PREFETCH_BEFORE_END
    ) {
      const level = hls.current?.levels[hls.current.currentLevel];
      prefetchSource(getPlaylist(nextChannelId, next.start, next.stop).source, level?.bitrate || 0);
    }
  }, [props.videoNow, props.videoStop, props.videoStart, ended]);

  const progress = useCallback(() => {
    const liveProgress = getDuration();
    const videoOptions: any = {};

    if (props.videoNow && props.videoStart + liveProgress > props.videoStop) {
      videoOptions.isLiveEpg = false;
    }

    if (Object.keys(videoOptions).length !== 0) {
      props.setVideoOptions(videoOptions);
    }
  }, [props.isLiveEpg, props.videoNow, props.videoStart, props.videoStop]);

  /**
   * Update the individual setting of quality for the current channel
   * @param {String} setting - the setting to update from channels[index].channelSettings
   * @param {*} value - the value to set for the current channel settings
   */
  const changeIndividualSetting = useCallback(
    async (setting: ChannelSettingKeys, value: any, save = false) => {
      try {
        if (!props.channelId) return;
        props.setChannelSetting(props.channelId, setting, value);
        if (save) {
          await props.saveProfileSettings();
        }
      } catch (error) {
        console.warn(error);
      }
    },
    [props.profileSettings, props.channelId]
  );

  const changeNextQuality = useCallback(
    (value: number) => {
      props.setVideoOptions({ autoLevel: value < 0 });
      if (hls.current) hls.current.nextLevel = value;
    },
    [props.setVideoOptions, hls.current]
  );

  const changeQuality = useCallback(
    (value: number) => {
      if (!hls.current) {
        return;
      }
      const autoLevel = value < 0;
      props.setVideoOptions({ autoLevel });
      const level = hls.current.levels[value];
      // canonical resolution: "auto" or "WIDTHxHEIGHT"
      const resolution = autoLevel
        ? RESOLUTION_AUTO
        : level?.width
        ? `${level.width}x${level.height}`
        : String(level?.height ?? '');
      changeIndividualSetting('resolution', resolution);
      if (hls.current) hls.current.currentLevel = value;
    },
    [changeIndividualSetting]
  );

  // returns the track's language code, or null when "off"/not found
  const findLanguageName = useCallback((resourceList?: any[], value?: any) => {
    const language = resourceList?.find((e) => e.id === value);
    return value === -1 || isNil(language) ? null : language.name;
  }, []);

  const changeAudioTrack = useCallback(
    (value: number) => {
      if (!hls.current) {
        return;
      }
      hls.current.dvbAudioTrack = value;
      const name = findLanguageName(hls.current.dvbAudioTracks, value);
      changeIndividualSetting('audio', name ?? LANG_DEFAULT);
    },
    [changeIndividualSetting]
  );

  const changeSubtitleTrack = useCallback(
    (value: number) => {
      if (!hls.current) {
        return;
      }
      hls.current.subtitleTrack = value;

      const name = findLanguageName(hls.current.subtitleTracks, value);
      changeIndividualSetting('subtitles', name ?? SUBTITLES_OFF);
    },
    [changeIndividualSetting]
  );

  // Handle next video
  const clearZapCommit = () => {
    clearTimeout(zapCommitTimerRef.current);
    zapCommitTimerRef.current = undefined;
  };

  const commitZap = useCallback(() => {
    clearZapCommit();
    zapHeldRef.current = false;
    flushZapStoreUpdate();
    const target = zapTargetRef.current;
    zapTargetRef.current = undefined;
    pendingZapRef.current = false;
    const resetZapState = () => {
      setState({ fastZapNumber: undefined });
      props.setVideoOptions({ fastZapping: false });
    };
    if (target) {
      if (target.position !== currentChannelPositionRef.current) {
        const epg = getLiveEpg(target);
        const update = () => {
          changeVideo(epg.chan_id, epg, target.position, -1);
          resetZapState();
        };
        // the player was reset or another source loaded before the zap reached the store
        const cancel = () => {
          if (pendingZapStoreUpdateRef.current === update) {
            pendingZapStoreUpdateRef.current = undefined;
            afterZapUpdatesRef.current = [];
            resetZapState();
          }
        };
        // load before React renders the new channel, which waits for the first segment
        // sorted channels reuse access as a sort key, the store has the real one
        const channel = liveChannelsRef.current?.[target.id];
        const preloaded =
          channel?.access === HAS_ACCESS &&
          !channel.locked &&
          videoHandleRef.current?.preload?.(
            getPlaylist(epg.chan_id, epg.start, epg.stop).source,
            epg.chan_id,
            () => pendingZapStoreUpdateRef.current === update && flushZapStoreUpdate(),
            cancel
          );
        if (preloaded) {
          pendingZapStoreUpdateRef.current = update;
        } else {
          update();
        }
        const { nextVideo } = store.getState().videoPlayer;
        !isEmpty(nextVideo) &&
          nextVideo?.channelId !== epg.chan_id &&
          props.setNextVideo(epg, target.position, -1, true);
        return;
      }
      props.resetNextVideo();
    }
    resetZapState();
  }, [changeVideo]);

  const scheduleZapCommit = useCallback(
    (delay: number) => {
      clearZapCommit();
      zapCommitTimerRef.current = setTimeout(commitZap, delay);
    },
    [commitZap]
  );

  useEffect(() => {
    if (props.hasNextVideo) {
      return;
    }
    setZapPreview(undefined);
    zapTargetPositionRef.current = undefined;
    // reset elsewhere (e.g. channel lock), drop the pending zap
    if (zapTargetRef.current) {
      zapTargetRef.current = undefined;
      pendingZapRef.current = false;
      clearZapCommit();
    }
  }, [props.hasNextVideo]);

  useEffect(
    () => () => {
      clearZapCommit();
      setZapPreview(undefined);
    },
    []
  );
  //

  const sortedLiveChannelsByPositionRef = useRef<LiveChannel[]>([]);
  // Live channel array sorted as like it would be without the paid channel position mask
  const sortedLiveChannelsByPosition = useMemo(() => {
    if (!props.liveChannels) {
      return [];
    }

    if (variables.WHITELABEL_TYPE === AppTheme.LINEAR) {
      return sortByPosition([...Object.values(props.liveChannels)]);
    }

    // If theme is ONDEMAND add favorite channels to the list
    return sortByPosition([
      ...Object.values(props.favoriteChannels),
      ...Object.values(props.liveChannels)
    ]);
  }, [props.liveChannels, props.favoriteChannels]);

  useEffect(() => {
    sortedLiveChannelsByPositionRef.current = sortedLiveChannelsByPosition;
  }, [sortedLiveChannelsByPosition]);

  const logosRef = useRef(props.logos);
  logosRef.current = props.logos;
  const preloadLogosAround = useCallback((position?: number) => {
    const channels = sortedLiveChannelsByPositionRef.current;
    const index = channels.findIndex((c) => c.position === position);
    if (index === -1) {
      return;
    }
    for (let offset = -ZAP_LOGO_PRELOAD_RANGE; offset <= ZAP_LOGO_PRELOAD_RANGE; offset++) {
      const channel =
        channels[(((index + offset) % channels.length) + channels.length) % channels.length];
      preloadImage(logosRef.current?.[channel.id]?.png);
    }
  }, []);

  useEffect(() => {
    preloadLogosAround(currentChannelPosition);
  }, [currentChannelPosition, sortedLiveChannelsByPosition]);

  const handleFastZapping = useCallback(
    (param: ('up' | 'down') | number) => {
      if (
        isEmpty(sortedLiveChannelsByPositionRef.current) ||
        isNil(currentChannelPositionRef.current) ||
        playerControlsStateRef.current === 'channels'
      ) {
        return;
      }
      const _currentChannelPosition =
        zapTargetPositionRef.current ?? currentChannelPositionRef.current;
      const isDirectional = isString(param) ? ['up', 'down'].includes(param) : false;

      let currentChannelIndex = -1;
      if (isFinite(param as number) && !isDirectional) {
        currentChannelIndex = sortedLiveChannelsByPositionRef.current.findIndex(
          (c) => c.position === param
        );
      } else if (isDirectional) {
        currentChannelIndex = sortedLiveChannelsByPositionRef.current.findIndex(
          (c) => c.position === _currentChannelPosition
        );
      }
      if (currentChannelIndex !== -1) {
        let nextChannelIndex = -1;
        if (isNumber(param)) {
          // if unlock overlay is presented we shouldn't use numbers to change channels
          if (showUnlockOverlay) {
            return;
          }
          nextChannelIndex = currentChannelIndex;
        } else if (isString(param)) {
          const offset = param === 'up' ? 1 : param === 'down' ? -1 : 0;
          nextChannelIndex = currentChannelIndex + offset;

          /*
           * Note:
           * If channel is hidden we want to skip over it from the list when switching channels with DOWN and UP.
           * If direction is a Fast Zap Number it should be played, no matter that it is not visible in the channel lists.
           */
          const skipNextChannel = true;
          while (skipNextChannel) {
            // Wrap around if needed
            if (nextChannelIndex >= sortedLiveChannelsByPositionRef.current.length) {
              nextChannelIndex = 0;
            } else if (nextChannelIndex < 0) {
              nextChannelIndex = sortedLiveChannelsByPositionRef.current.length - 1;
            }

            const nextChannel = sortedLiveChannelsByPositionRef.current[nextChannelIndex];
            if (nextChannel.visible) {
              break; // Exit the loop when a visible channel is found
            }

            // Move to the next channel in the specified direction
            nextChannelIndex += offset;
          }
        }

        const nextChannel = sortedLiveChannelsByPositionRef.current[nextChannelIndex];
        if (nextChannel) {
          preloadLogosAround(nextChannel.position);
          zapTargetPositionRef.current = nextChannel.position;
          zapTargetRef.current = nextChannel;
          pendingZapRef.current = true;
          // keydowns only move the preview, the store gets the first and the final channel
          setZapPreview({ channelId: nextChannel.id, position: nextChannel.position });
          const { video, nextVideo } = store.getState().videoPlayer;
          !video?.fastZapping && props.setVideoOptions({ fastZapping: true });
          isEmpty(nextVideo) &&
            props.setNextVideo(getLiveEpg(nextChannel), nextChannel.position, -1, true);
        }
        if (isDirectional) {
          // another keydown before keyup: the button is held
          zapHeldRef.current = zapHeldRef.current || zapKeyDownRef.current;
          zapKeyDownRef.current = true;
          scheduleZapCommit(ZAP_NO_KEYUP_TIMEOUT);
        } else {
          scheduleZapCommit(FAST_ZAP_NUMBER_INPUT_TIMEOUT);
        }
        // Force show mini view when switching to next channel
        isDirectional && toggleControls(true);
      }
    },
    [props.hasNextVideo, showUnlockOverlay, scheduleZapCommit, preloadLogosAround]
  );

  const toggleControlOnCanPlay = useCallback(() => {
    const skipToggleOnStates: PlayerControlsState[] = ['channels', 'bottom-view'];
    !skipToggleOnStates.includes(playerControlsStateRef.current) &&
      toggleControls(playerControlsStateRef.current !== 'expanded');
    if (!pendingZapRef.current) {
      props.resetNextVideo();
    }
  }, [toggleControls]);

  // When player is playing and the channelId is changed
  useEffect(() => {
    if (!isEmpty(props.channelId) && props.isPlaying) {
      lockChannelsBack();
    }
  }, [props.channelId, props.videoSource, props.isFullscreen, props.isPlaying]);

  useEffect(() => {
    const idle = requestIdleCallback(
      () => {
        const lastRequestedEpgsDelta = Date.now() - lastRequestedEpgsTimestampRef.current;
        if (
          props.isPlaying &&
          props.channelId &&
          !props.vod &&
          channelAccessRef.current &&
          lastRequestedEpgsDelta >= variables.MIN_UPDATE_TIME_FOR_DATA
        ) {
          props.getAllEpgsForChannel(props.channelId);
          lastRequestedEpgsTimestampRef.current = Date.now();
        }
      },
      { timeout: 2000 }
    );
    return () => {
      cancelIdleCallback(idle);
    };
  }, [props.channelId, props.vod, props.videoHash, props.isPlaying]);

  // Load EPGs if the bottom view opens before playback starts
  useEffect(() => {
    if (
      props.playerControlsState !== 'bottom-view' ||
      props.vod ||
      !props.channelId ||
      !channelAccessRef.current ||
      props.allEpgs?.[0]?.chan_id === props.channelId
    ) {
      return;
    }
    props.getAllEpgsForChannel(props.channelId);
    lastRequestedEpgsTimestampRef.current = Date.now();
  }, [props.playerControlsState, props.channelId]);

  // Handle this function only on first load and fullscreen
  const onCanPlay = useCallback(() => {
    props.setPlayerPlaying(true);
    if (!playerIsFullscreenRef.current || !channelAccessRef.current) {
      return;
    }
    if (!pendingZapRef.current) {
      props.setVideoOptions({ fastZapping: false });
    }
    clearBuffering();
    toggleControlOnCanPlay();
    screenSaverToggle(false);
  }, [debouncedCalls.current, channelAccess, toggleControlOnCanPlay]);

  const onLoadStart = useCallback(() => {
    if (isEmpty(videoSourceRef.current) || !playerIsFullscreenRef.current) {
      return;
    }

    mediaPreloadTimeout.current = setTimeout(() => {
      setState({ showBuffer: true });
    }, variables.PRELOAD_TIMEOUT);
  }, []);

  const handleBackKey = useCallback(() => {
    // If vod selection page is open, close the player
    if (vodSelectionPageRef.current) {
      closePlayer();
      props.resetVideo();
      return;
    }
    switch (playerControlsStateRef.current) {
      case 'hidden':
        {
          if (props.vod) {
            props.stopVideo();
          } else {
            closePlayer();
          }
        }
        break;
      default:
        hideControls();
        break;
    }
  }, [closePlayer, props.vod]);

  const handleMediaTrackNext = useCallback(() => {
    if (props.isLiveEpg) {
      setVideoToLive();
    } else {
      playNext();
    }
  }, [videoRef.current, props.isLiveEpg]);

  const handleMediaTrackPrevious = useCallback(() => {
    const currentTime = getCurrentTime();
    if (currentTime < PLAY_PREVIOUS_THRESHOLD) {
      playPrevious();
    } else {
      setVideoToBegining();
    }
  }, [videoRef.current]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const key = e.keyCode;
      const visible = playerIsFullscreenRef.current;

      if (!visible) {
        return;
      }

      const keyName = getRemoteKeyName(key);
      switch (keyName) {
        case 'OK':
          handleOk(e);
          break;
        // Back
        case 'BACK':
          {
            // Close player if unlock overlay is presented
            // and some ui is visible
            if (!(playerControlsStateRef.current === 'hidden' && showUnlockOverlayRef.current)) {
              handleBackKey();
            }
          }
          break;
        case 'PLAY':
          play();
          break;
        case 'PAUSE':
          pause();
          break;
        case 'PLAY_PAUSE':
          togglePlay();
          break;
        case 'TRACK_PREVIOUS':
          handleMediaTrackPrevious();
          break;
        case 'TRACK_NEXT':
          handleMediaTrackNext();
          break;
        case 'STOP':
          closePlayer();
          break;
        // Arrow up
        // Channel list up
        case 'ARROW_UP':
        case 'CHANNEL_UP':
          if (['mini', 'hidden'].includes(playerControlsStateRef.current)) {
            zapHeldRef.current = zapHeldRef.current || e.repeat;
            handleFastZapping('up');
          }
          break;
        // Arrow down
        // Channel list down
        case 'ARROW_DOWN':
        case 'CHANNEL_DOWN':
          if (['mini', 'hidden'].includes(playerControlsStateRef.current)) {
            zapHeldRef.current = zapHeldRef.current || e.repeat;
            handleFastZapping('down');
          }
          break;
        // Channel list button
        case 'CHANNEL_LIST':
          channelListDebounce.current && channelListDebounce.current.cancel();
          channelListDebounce.current = debounce(() => {
            if (playerControlsStateRef.current !== 'channels') {
              props.setPlayerControlsState('channels');
            } else {
              hideControls();
            }
          }, CONTROLS_EXPAND_DURATION);
          channelListDebounce.current();
          break;
        case 'ARROW_LEFT':
        case 'ARROW_RIGHT':
          break;
        default:
          if (!showUnlockOverlayRef.current) {
            handleNumberFastZap(getKeyDigit(e));
          }
          break;
      }

      refreshControlsTimeout();
    },
    [
      props.isPlaying,
      props.channelId,
      props.hasNextVideo,
      props.liveChannels,
      videoRef.current,
      channelListDebounce.current,
      handleMediaTrackNext,
      handleMediaTrackPrevious,
      play,
      pause,
      togglePlay,
      closePlayer
    ]
  );

  const channelNumbers = useMemo(() => {
    if (isEmpty(props.liveChannels)) return [];

    const liveChannelNumbers = Object.values(props.liveChannels)
      // .filter((channel) => channel.access)
      .map((channel) => {
        return channel.position;
      });

    const favoriteChannelsNumbers = Object.values(props.favoriteChannels).map((e) => e.position);

    return [...favoriteChannelsNumbers, ...liveChannelNumbers];
  }, [props.liveChannels, props.favoriteChannels]);

  const handleNumberFastZap = useCallback(
    (key?: string) => {
      const number = toNumber(key);
      if (isFinite(number) && !isEmpty(channelNumbers)) {
        // if channel is locked number fast zapping should be disabled
        if (showUnlockOverlay) {
          return;
        }

        // check if this causes Maximum update depth exceeded. warning
        setState((current) => {
          const fastZapNumber = toNumber(
            (current.fastZapNumber?.toString() || '')
              .concat(number.toString())
              .slice(0, MAX_FASTZAPP_LENGTH)
          );
          const channelNumber = findClosestNumber(channelNumbers, fastZapNumber);
          handleFastZapping(channelNumber);
          return { ...current, fastZapNumber };
        });
      }
    },
    [channelNumbers]
  );

  const clearOkHold = useCallback(() => {
    resumeFocusable();
    clearTimeout(holdingOkTimeoutRef.current);
    holdingOkTimeoutRef.current = undefined;
  }, []);

  const toggleControlsOnSelect = useCallback(() => {
    if (['hidden', 'mini'].includes(playerControlsStateRef.current)) {
      toggleControls();
    }
  }, [toggleControls]);

  const openChannelListOnHold = useCallback(() => {
    // Open channel list only if unlock overlay is not visible and player controls state is hidden or mini
    if (
      holdingOkTimeoutRef.current !== undefined &&
      !showUnlockOverlayRef.current &&
      !vodRef.current &&
      ['hidden', 'mini'].includes(playerControlsStateRef.current)
    ) {
      props.setPlayerControlsState('channels');
    }
  }, []);

  const onLongPressOk = useCallback(() => {
    pauseFocusable();
    openChannelListOnHold();
  }, [openChannelListOnHold]);

  const onKeyUp = useCallback(
    (e: KeyboardEvent) => {
      const keyName = getRemoteKeyName(e.keyCode);
      // some remotes send keyup for every repeat while held
      if (
        zapKeyDownRef.current &&
        keyName &&
        ['ARROW_UP', 'ARROW_DOWN', 'CHANNEL_UP', 'CHANNEL_DOWN'].includes(keyName)
      ) {
        zapKeyDownRef.current = false;
        if (pendingZapRef.current) {
          scheduleZapCommit(zapHeldRef.current ? ZAP_HOLD_RELEASE_DELAY : ZAP_RELEASE_DELAY);
        }
        zapHeldRef.current = false;
        return;
      }
      // Handle OK key only if it was pressed before that
      if (keyName === 'OK') {
        if (!pressedOkRef.current) {
          clearOkHold();
          return;
        }
        // Continue with the action only if OK was pressed before
        clearOkHold();
        pressedOkRef.current = false;
        toggleControlsOnSelect();
      }
    },
    [toggleControlsOnSelect, scheduleZapCommit]
  );

  const handleOk = useCallback(
    (e: KeyboardEvent) => {
      // Toggle press flag for OK key when player controls state is hidden or mini
      if (!e.repeat && ['mini', 'hidden'].includes(playerControlsStateRef.current)) {
        pressedOkRef.current = true;
      }
      // Handle fast zap
      if (pendingZapRef.current) {
        commitZap();
        return;
      }
      // a held OK after selecting in the channel list must not reopen it
      if (!pressedOkRef.current) {
        return;
      }
      // Implementation of long press on remotes that
      // support repeating events. (E.g. LG)
      if (e.repeat) {
        pauseFocusable();
        openChannelListOnHold();
      }
      if (!holdingOkTimeoutRef.current)
        holdingOkTimeoutRef.current = setTimeout(onLongPressOk, variables.HOLD_TIMEOUT);
    },
    [toggleControlsOnSelect, onLongPressOk, commitZap]
  );

  const onClick = useCallback(() => {
    const visible = playerIsFullscreenRef.current;
    if (!visible) {
      return;
    }
    toggleControlsOnSelect();
  }, [toggleControlsOnSelect]);

  const getRef = useCallback(
    (ref: VideoRefObject) => {
      videoHandleRef.current = ref;
      if (ref) {
        if (ref.hls) hls.current = ref.hls;
        if (ref.video) {
          setVideoRefObject(ref.video);
          videoRef.current = ref.video;
        }
      }
    },
    [props.isFullscreen]
  );

  const renderVodOverlay = () => {
    // Hide VOD overlay if videoSource is set
    if (!props.vod || !props.vodAsset || videoSource) {
      return null;
    }

    return <VodOverlay asset={props.vodAsset} />;
  };

  const renderRadioOverlay = () =>
    radioImages && (
      <>
        <Image className="radio-overlay" src={radioImages.radio_wallpaper_image.url} />
        <Image
          className="radio-logo-overlay"
          src={props.logos[props.channelId as string]?.pngWhite}
        />
      </>
    );

  const handlePlayVideo = useCallback(() => {
    if (!props.channelId) {
      return;
    }

    const currentEpg = props.liveChannels[props.channelId].currentepg;

    props.playVideo(currentEpg, { startPosition: -1 });
    props.setVideoOptions({
      isFullscreen: true
    });
  }, [props.playVideo, props.setVideoOptions, props.channelId, props.liveChannels]);

  const handleUnlockChannelsSuccess = useCallback(() => {
    temporarilyUnlock();
    resumeFocusable();
    handlePlayVideo();
    setShowUnlockOverlay(false);
  }, [temporarilyUnlock, handlePlayVideo]);

  const renderChannelAccessOverlay = useCallback(() => {
    if (showPaidOverlay) {
      return <ChannelAccessOverlay channelId={props.channelId} />;
    }

    if (showUnlockOverlay) {
      return (
        <LockChannelOverlay
          channelId={props.channelId}
          onSuccess={handleUnlockChannelsSuccess}
          onBack={handleBackKey}
          videoRef={videoRef}
        />
      );
    }

    if (showUnsupportedCodecOverlay) {
      return <UnsupportedCodecOverlay onBack={handleBackKey} />;
    }
  }, [
    showUnlockOverlay,
    showPaidOverlay,
    showUnsupportedCodecOverlay,
    props.channelId,
    props.playerControlsState
  ]);

  const renderLoadingIndicator = () => props.isOpen && state.showBuffer && <LoadingIndicator />;

  const getCanvasRef = useCallback((canvasNode: HTMLCanvasElement) => {
    subtitleCanvasRef.current = canvasNode;
    if (!canvasNode) return;
    hls.current?.attachDvbCanvas(subtitleCanvasRef.current);
  }, []);

  useEffect(() => {
    if (hasFocusedChild || hasPlayerOverlay) {
      document.addEventListener('keydown', onKeyDown);
      document.addEventListener('keyup', onKeyUp);
    }
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
    };
  }, [hasFocusedChild, hasPlayerOverlay, onKeyDown, onKeyUp]);

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        className={getClassName(`${isEmpty(props.className) ? '' : props.className}`, {
          fullscreen: props.isFullscreen,
          playing: props.isPlaying
        })}
        ref={focusRef}
        onClick={onClick}
        onMouseMove={refreshControlsTimeout}
        tabIndex={videoSource ? 0 : -1}
        data-test-id="video-player"
        data-fullscreen={!!props.isFullscreen}
        data-playing={!!props.isPlaying}
        data-vod={!!props.vod}
        data-controls-state={props.playerControlsState}
      >
        <div className="video-container">
          <Video
            autoPlay={!props.paused}
            channelId={props.channelId}
            src={videoSource}
            hash={props.videoHash}
            // a channel without access is the only time hls.js is dropped
            destroyWithoutSource={!!props.videoSource && props.videoAccess !== HAS_ACCESS}
            startPosition={props.videoStartPosition}
            tabIndex={videoSource ? 0 : -1}
            onTimeUpdate={timeUpdate}
            onEnded={ended}
            onProgress={progress}
            ref={getRef}
            onCanPlay={!props.isPlaying ? onCanPlay : undefined}
            onLoadStart={onLoadStart}
            onSubtitleTrackSwitch={onSubtitleTrackSwitch}
            onSubtitlesUpdated={onSubtitlesUpdated}
            onDvbAudioTrackSwitch={onDvbAudioTrackSwitch}
            onDvbAudioTracksUpdated={onDvbAudioTracksUpdated}
            onLevelSwitched={onLevelSwitched}
            onLevelsUpdated={onLevelsUpdated}
            onHlsError={handleHlsError}
            onBufferAppended={onBufferAppended}
            onCodecCheck={handleCodecCheck}
          />
          <canvas ref={getCanvasRef} className="dvb-canvas" width="720" height="576" />
          {renderVodOverlay()}
          {renderRadioOverlay()}
          {renderChannelAccessOverlay()}
          {renderLoadingIndicator()}
        </div>
        <Controls
          toggleControls={toggleControls}
          hideControls={hideControls}
          vodSelectionPage={vodSelectionPage}
          showControls={showControls}
          play={play}
          pause={pause}
          setCurrentTime={setCurrentTime}
          getDuration={getDuration}
          setVideoToBegining={setVideoToBegining}
          setVideoToLive={setVideoToLive}
          forward={forward}
          backward={backward}
          togglePlay={togglePlay}
          playPrevious={playPrevious}
          changeSubtitleTrack={changeSubtitleTrack}
          changeAudioTrack={changeAudioTrack}
          changeQuality={changeQuality}
          fastZapNumber={state.fastZapNumber}
          disabled={showUnlockOverlay}
        />
      </div>
    </FocusContext.Provider>
  );
};

interface PlayerStore {
  videoPlayer: IVideoPlayer;
  liveChannels: LiveChannels;
  logos: any;
  i18n: typeof translationPacket;
  profileSettings: any;
  channelSettings: any;
  favoriteChannels: FavoriteChannels;
  authentication: { token: string };
}
const mapStateToProps = ({
  videoPlayer,
  liveChannels,
  logos,
  i18n,
  profileSettings,
  channelSettings,
  favoriteChannels,
  authentication
}: PlayerStore) => {
  return {
    currentAuthToken: authentication.token,
    allEpgs: videoPlayer.allEpgs,
    videoSource: videoPlayer.video?.source,
    videoStart: videoPlayer.video?.start,
    videoPlaylistStart: videoPlayer.video?.playlistStart,
    videoStop: videoPlayer.video?.stop,
    videoStartPosition: videoPlayer.video?.startPosition,
    videoPosition: videoPlayer.video?.channelPosition,
    channelId: videoPlayer.channelId,
    epgsObject: videoPlayer.epgs,
    isLiveEpg: videoPlayer.video?.isLiveEpg,
    isOpen: videoPlayer.isOpen,
    isFullscreen: videoPlayer.video?.isFullscreen,
    isPlaying: videoPlayer.isPlaying,
    videoPaused: videoPlayer.video?.paused,
    isStopped: videoPlayer.isStopped,
    hasNextVideo: !isEmpty(videoPlayer.nextVideo),
    videoNow: videoPlayer.video?.now,
    videoAccess: videoPlayer.video?.access,
    videoLocked: videoPlayer.video?.locked,
    videoHash: videoPlayer.video?.hash,
    audioTracks: videoPlayer.video?.audioTracks,
    subtitleTracks: videoPlayer.video?.subtitleTracks,
    logos,
    i18n,
    playerControlsState: videoPlayer.playerControlsState,
    liveChannels,
    profileSettings,
    channelSettings,
    favoriteChannels,
    vod: videoPlayer?.video?.vod,
    vodAsset: videoPlayer?.video?.vodAsset
  };
};

export default React.memo(
  connect(mapStateToProps, {
    setError,
    saveProfileSettings,
    setPlayerPlaying,
    getAllEpgsForChannel,
    setVideoPlayerEpgs,
    setCurrentVideo,
    setVideoOptions,
    resetVideo,
    getLiveChannels,
    resetEpgs,
    playVideo,
    setNextVideo,
    continueLiveWithNextEpg,
    resetNextVideo,
    removeUserDetails,
    setPlayerControlsState,
    getLastWatchedEpgs,
    getMostWatchedEpgs,
    setChannelSetting,
    stopVideo,
    unStopVideo,
    setVideoHash
  })(Player)
);
